import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./QuestionTypes.module.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import FilterIcon from "@mui/icons-material/Filter";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { handleErrorMessage } from "../../../../../redux/actions/groups";
import { useDispatch } from "react-redux";
import { validateQuestion } from "../../../../../utils";
import { useTranslation } from "react-i18next";

const ImageQuestionComponent = forwardRef(
  (
    { onSave, onCancel, onMoveUp, onMoveDonw, onDeleteQuestion, initialData },
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [question, setQuestion] = useState("");
    // previews (blob or hosted URLs)
    const [images, setImages] = useState([null, null]);
    // actual File objects selected by user (parallel array)
    const [imageFiles, setImageFiles] = useState([null, null]);
    const [correctAnswer, setCorrectAnswer] = useState(null);
    const [maxMarks, setMaxMarks] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(null);

    const containerRef = useRef(null);

    // --- Menu state ---
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuIndex, setMenuIndex] = useState(null);

    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    // --- Load initial data (only once) ---
    const initializedRef = useRef(false);
    useEffect(() => {
      if (initializedRef.current || !initialData) return;
      initializedRef.current = true;

      setQuestion(
        initialData.questionText ?? initialData.text ?? initialData.question ?? ""
      );

      const srcChoices = Array.isArray(initialData.choices)
        ? initialData.choices
        : Array.isArray(initialData.choice)
        ? initialData.choice
        : [];

      if (srcChoices.length > 0) {
        // populate previews from existing choice.img (hosted url). imageFiles remain null.
        setImages(srcChoices.map((c) => c.img ?? c.image ?? null));
        setImageFiles(srcChoices.map(() => null));
        setCorrectAnswer(
          srcChoices.findIndex((c) => c.isCorrect || c.is_correct) ?? null
        );
      }

      const marksValue =
        initialData.maxMarks ?? initialData.marks ?? initialData.max_marks ?? "";
      setMaxMarks(
        marksValue !== "" && marksValue !== null ? String(marksValue) : ""
      );

      setIsRequired(
        Boolean(initialData.isMandatory ?? initialData.is_mandatory ?? false)
      );
    }, [initialData]);

    // --- Outside click clears focus ---
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setFocusedIndex(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- Image handling ---
    const handleImageUpload = (e, idx) => {
      const file = e.target.files[0];
      if (!file) return;

      const preview = URL.createObjectURL(file);
      setImages((prev) => {
        const next = [...prev];
        // revoke old preview if present
        if (next[idx] && next[idx].startsWith("blob:")) {
          URL.revokeObjectURL(next[idx]);
        }
        next[idx] = preview;
        return next;
      });

      setImageFiles((prev) => {
        const next = [...prev];
        next[idx] = file;
        return next;
      });
    };

    const addNewImageOption = () => {
      setImages((prev) => [...prev, null]);
      setImageFiles((prev) => [...prev, null]);
    };

    const removeImage = (idx) => {
      setImages((prev) => {
        const next = [...prev];
        const removed = next.splice(idx, 1);
        // revoke blob preview if necessary
        if (removed[0] && removed[0].startsWith("blob:")) {
          try {
            URL.revokeObjectURL(removed[0]);
          } catch (e) {}
        }
        return next;
      });
      setImageFiles((prev) => {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      });

      if (correctAnswer === idx) setCorrectAnswer(null);
      else if (correctAnswer > idx) setCorrectAnswer(correctAnswer - 1);
    };

    // --- Save handler (returns an object containing File refs on `_file`) ---
    const handleSave = () => {
      const nonEmptyImages = images.filter((img) => img !== null);

      const errorMessage = validateQuestion({
        question,
        marks: maxMarks,
        options: nonEmptyImages,
        correctAnswer,
        type: "image",
      });

      if (errorMessage) {
        dispatch(handleErrorMessage(errorMessage));
        return null;
      }

      const formattedChoices = images.map((img, index) => ({
        id: `${initialData?.id ?? "img-q"}-${index}`,
        img, // this can be blob preview or existing hosted url — parent will strip blobs
        _file: imageFiles[index] ?? null, // actual File object if user selected
        isCorrect: index === correctAnswer,
      }));

      return {
        id: initialData?.id ?? `q-${Date.now()}`,
        type: "image",
        questionText: question.trim(),
        choices: formattedChoices,
        isMandatory: !!isRequired,
        maxMarks: parseInt(maxMarks, 10) || 0,
        icon: initialData?.icon ?? null,
      };
    };

    // --- Menu ---
    const handleMenuOpen = (event, index) => {
      setAnchorEl(event.currentTarget);
      setMenuIndex(index);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setMenuIndex(null);
    };

    const markAsCorrect = () => {
      setCorrectAnswer(menuIndex);
      handleMenuClose();
    };

    const removeImageOption = () => {
      removeImage(menuIndex);
      handleMenuClose();
    };

    // --- JSX ---
    return (
      <div className={styles.questionContainer}>
        <div className={styles.questionHeader}>
          <div className={styles.questionType}>
            <FilterIcon />
            <span>Image Choice</span>
          </div>

          <div className={styles.questionActions}>
            <button className={styles.actionButton} onClick={onMoveUp}>
              <KeyboardArrowUpIcon />
            </button>
            <button className={styles.actionButton} onClick={onMoveDonw}>
              <KeyboardArrowDownIcon />
            </button>
            <button className={styles.actionButton} onClick={onDeleteQuestion}>
              <DeleteOutlineIcon />
            </button>
            <div className={styles.marksContainer}>
              <span>{t("quiz.maxMarks")}</span>
              <input
                min={"0"}
                type="number"
                className={styles.marksInput}
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
            <div className={styles.requiredContainer}>
              <span>{t("quiz.required")}</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        <div className={styles.questionContent}>
          <input
            type="text"
            className={styles.questionInput}
            placeholder="Enter your question..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className={styles.optionsContainer} ref={containerRef}>
            {images.map((img, idx) => (
              <div
                key={idx}
                className={`${styles.optionItem} ${
                  correctAnswer === idx ? styles.correctOption : ""
                }`}
                onMouseEnter={() => setFocusedIndex(idx)}
                onMouseLeave={() => setFocusedIndex(null)}
              >
                <input
                  type="radio"
                  name="correct"
                  checked={correctAnswer === idx}
                  onChange={() => setCorrectAnswer(idx)}
                />
                {img ? (
                  <div className={styles.imgPreviewWrapper}>
                    <div className={styles.optionHeader}>
                      <IconButton onClick={(e) => handleMenuOpen(e, idx)}>
                        <MoreVertIcon fontSize="small" sx={{ color: "#fff" }} />
                      </IconButton>
                    </div>
                    <img
                      width={300}
                      src={img}
                      alt="choice"
                      className={styles.previewImg}
                    />
                  </div>
                ) : (
                  <>
                    <label htmlFor={`img-${idx}`} className={styles.imageUploadIcon}>
                      <AddPhotoAlternateIcon />
                    </label>
                    <input
                      id={`img-${idx}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, idx)}
                      hidden
                    />
                    {focusedIndex === idx && (
                      <CloseIcon
                        className={styles.removeIcon}
                        onClick={() => removeImage(idx)}
                      />
                    )}
                  </>
                )}
              </div>
            ))}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={removeImageOption}>Remove choice</MenuItem>
              <MenuItem onClick={markAsCorrect}>Mark as correct</MenuItem>
            </Menu>

            <button className={styles.addOptionButton} onClick={addNewImageOption}>
              <AddCircleOutlineIcon sx={{ color: "#F33070" }} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);
export default ImageQuestionComponent;